import type {
  AgentRecord,
  CliProvider,
  ConversationContextVersion,
  ConversationDetail,
  ConversationMode,
  ConversationSummary,
  ConversationTeamPlan,
  ConversationTurnControl,
  ModelPolicy,
  RunEvent,
  RunRecord,
  SessionUser,
  WorkflowGraph,
  WorkflowSummary,
  WorkerRecord,
} from "../shared/types";

export interface TeamPlanMemberDraft {
  name: string;
  handle?: string;
  role: string;
  provider: CliProvider;
  model: string;
  modelPolicy: ModelPolicy;
  rationale: string;
  instructions: string;
  tools: string[];
  budget: number;
  outputContract: string;
  contextScope: string[];
  workspacePath?: string;
  contextBudget?: number;
  fallback?: { provider: CliProvider; model: string; reason: string } | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly payload?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      payload && typeof payload === "object" && "message" in payload
        ? String(payload.message)
        : `A requisição falhou (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: SessionUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  me: () => request<{ user: SessionUser }>("/api/auth/me"),
  workflows: () => request<{ workflows: WorkflowSummary[] }>("/api/workflows"),
  workflow: (id: string) => request<{ workflow: WorkflowSummary }>(`/api/workflows/${id}`),
  createWorkflow: (name: string) =>
    request<{ workflow: WorkflowSummary }>("/api/workflows", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  saveWorkflow: (id: string, name: string, graph: WorkflowGraph) =>
    request<{ workflow: WorkflowSummary }>(`/api/workflows/${id}`, {
      method: "PUT",
      body: JSON.stringify({ name, graph }),
    }),
  publishWorkflow: (id: string) =>
    request<{ workflow: WorkflowSummary }>(`/api/workflows/${id}/publish`, {
      method: "POST",
    }),
  runWorkflow: (id: string, input: Record<string, unknown>) =>
    request<{ runId: string }>(`/api/workflows/${id}/run`, {
      method: "POST",
      body: JSON.stringify({ input }),
    }),
  runs: () => request<{ runs: RunRecord[] }>("/api/runs"),
  run: (id: string) =>
    request<{
      run: RunRecord;
      events: RunEvent[];
      approval: { nodeId: string; status: string; requestedAt: string } | null;
    }>(`/api/runs/${id}`),
  cancelRun: (id: string) =>
    request<{ accepted: boolean }>(`/api/runs/${id}/cancel`, { method: "POST" }),
  resolveApproval: (id: string, nodeId: string, decision: "approved" | "rejected") =>
    request<{ accepted: boolean }>(`/api/runs/${id}/approval`, {
      method: "POST",
      body: JSON.stringify({ nodeId, decision }),
    }),
  agents: () => request<{ agents: AgentRecord[] }>("/api/agents"),
  createAgent: (agent: Omit<AgentRecord, "id" | "updatedAt">) =>
    request<{ agent: AgentRecord }>("/api/agents", {
      method: "POST",
      body: JSON.stringify(agent),
    }),
  updateAgent: (id: string, agent: Omit<AgentRecord, "id" | "updatedAt">) =>
    request<{ agent: AgentRecord }>(`/api/agents/${id}`, {
      method: "PUT",
      body: JSON.stringify(agent),
    }),
  runtimeSettings: () =>
    request<{
      provider: string;
      openAiCompatibleConfigured: boolean;
      executionMode: string;
    }>("/api/settings/runtime"),
  conversations: () => request<{ conversations: ConversationSummary[] }>("/api/conversations"),
  conversation: (id: string) =>
    request<{ conversation: ConversationDetail }>(`/api/conversations/${id}`),
  createConversation: (input: {
    title: string;
    mode: ConversationMode;
    workspacePath: string;
    agentIds: string[];
    leadAgentId: string | null;
  }) => request<{ conversation: ConversationSummary }>("/api/conversations", {
    method: "POST",
    body: JSON.stringify(input),
  }),
  updateConversation: (id: string, input: {
    title: string;
    mode: ConversationMode;
    workspacePath: string;
    agentIds: string[];
    leadAgentId: string | null;
  }) => request<{ conversation: ConversationSummary }>(`/api/conversations/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  }),
  createConversationContext: (id: string, input: {
    summary: string;
    manifest: Record<string, unknown>;
    workspacePath: string;
  }) => request<{ context: ConversationContextVersion }>(`/api/conversations/${id}/context`, {
    method: "POST",
    body: JSON.stringify(input),
  }),
  createTeamPlan: (id: string, input: {
    briefing: string;
    objective: string;
    members: TeamPlanMemberDraft[];
    leadMemberIndex: number;
  }) => request<{ plan: ConversationTeamPlan }>(`/api/conversations/${id}/team-plans`, {
    method: "POST",
    body: JSON.stringify(input),
  }),
  resolveTeamPlan: (conversationId: string, planId: string, decision: "approved" | "rejected") =>
    request<{ plan: ConversationTeamPlan }>(
      `/api/conversations/${conversationId}/team-plans/${planId}/decision`,
      { method: "POST", body: JSON.stringify({ decision }) },
    ),
  sendConversationMessage: (id: string, input: {
    body: string;
    agentIds: string[];
    mode: ConversationMode;
    leadAgentId?: string | null;
  }) => request<{ turnId: string; queuedAgents: number }>(`/api/conversations/${id}/messages`, {
    method: "POST",
    body: JSON.stringify(input),
  }),
  pauseConversation: (id: string) =>
    request<{ accepted: boolean; turn: ConversationTurnControl }>(`/api/conversations/${id}/pause`, {
      method: "POST",
    }),
  resumeConversation: (id: string) =>
    request<{ accepted: boolean; turn: ConversationTurnControl }>(`/api/conversations/${id}/resume`, {
      method: "POST",
    }),
  cancelConversation: (id: string) =>
    request<{ accepted: boolean; turn: ConversationTurnControl }>(`/api/conversations/${id}/cancel`, {
      method: "POST",
    }),
  workers: () => request<{ workers: WorkerRecord[] }>("/api/workers"),
  enrollWorker: (name: string) => request<{ workerId: string; token: string }>("/api/workers/enroll", {
    method: "POST",
    body: JSON.stringify({ name }),
  }),
};

export function subscribeToConversation(
  conversationId: string,
  onEvent: (type: string, payload: Record<string, unknown>) => void,
): () => void {
  const source = new EventSource(`/api/conversations/${conversationId}/events`);
  for (const eventType of ["message.created", "job.updated", "turn.updated", "team.updated", "context.updated"]) {
    source.addEventListener(eventType, (message) => {
      onEvent(eventType, JSON.parse((message as MessageEvent).data) as Record<string, unknown>);
    });
  }
  return () => source.close();
}

export function subscribeToRun(
  runId: string,
  onEvent: (event: RunEvent) => void,
): () => void {
  const source = new EventSource(`/api/runs/${runId}/events`);
  const eventTypes = [
    "run.queued",
    "run.started",
    "run.waiting",
    "run.completed",
    "run.failed",
    "run.cancelled",
    "node.running",
    "node.completed",
    "node.error",
    "approval.required",
    "approval.approved",
    "approval.rejected",
  ];
  for (const eventType of eventTypes) {
    source.addEventListener(eventType, (message) => {
      onEvent(JSON.parse((message as MessageEvent).data) as RunEvent);
    });
  }
  return () => source.close();
}
